import React, { useEffect, useState, useMemo } from 'react';
import {
  Stack,
  Text,
  SearchBox,
  CommandBar,
  ICommandBarItemProps,
  DetailsList,
  DetailsListLayoutMode,
  Selection,
  SelectionMode,
  IColumn,
  IconButton,
  Dialog,
  DialogType,
  DialogFooter,
  PrimaryButton,
  DefaultButton,
  Spinner,
  MessageBar,
  MessageBarType,
  Modal,
  TextField,
} from '@fluentui/react';
import { IKreditor, IUserPermissions } from '../../shared/models';
import { kreditorenApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { centeredModalStyles, modalContentStyles } from '../styles/dialogs';
import { KreditorAnlegenDialog } from '../components/KreditorAnlegenDialog';
import axios from 'axios';

export const KreditorenListe: React.FC = () => {
  const { user } = useAuth();
  const [kreditoren, setKreditoren] = useState<IKreditor[]>([]);
  const [filteredKreditoren, setFilteredKreditoren] = useState<IKreditor[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [selectedItems, setSelectedItems] = useState<IKreditor[]>([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [permissions, setPermissions] = useState<IUserPermissions | null>(null);

  // Dialog States
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedKreditor, setSelectedKreditor] = useState<IKreditor | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<IKreditor>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadKreditoren();
    loadPermissions();
  }, []);

  useEffect(() => {
    filterKreditoren();
  }, [searchText, kreditoren]);

  const loadPermissions = async () => {
    try {
      if (user?.email) {
        const response = await axios.get('/api/permissions/me', {
          params: { email: user.email }
        });
        setPermissions(response.data);
      }
    } catch (err: any) {
      console.error('Fehler beim Laden der Berechtigungen:', err);
    }
  };

  const loadKreditoren = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await kreditorenApi.getAll();
      setKreditoren(data);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden der Kreditoren');
    } finally {
      setLoading(false);
    }
  };

  const filterKreditoren = () => {
    let filtered = [...kreditoren];

    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter((k) =>
        k.Kreditorname?.toLowerCase().includes(searchLower) ||
        k.Kreditorennummer?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredKreditoren(filtered);
  };

  const isAdminOrEinkauf = useMemo(() => {
    if (!permissions) return false;
    return permissions.isAdmin || permissions.isEinkauf;
  }, [permissions]);

  const handleDeleteSingle = async (kreditor: IKreditor) => {
    if (!kreditor.Id) return;

    try {
      setSaving(true);
      setError(null);
      await kreditorenApi.delete(kreditor.Id);
      await loadKreditoren();
      setDeleteDialogOpen(false);
      setSelectedKreditor(null);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Löschen des Kreditors');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkDelete = async () => {
    try {
      setSaving(true);
      setError(null);

      for (const kreditor of selectedItems) {
        if (kreditor.Id) {
          await kreditorenApi.delete(kreditor.Id);
        }
      }

      await loadKreditoren();
      setBulkDeleteDialogOpen(false);
      setSelectedItems([]);
      setSelectionMode(false);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Löschen der Kreditoren');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedKreditor?.Id) return;

    try {
      setSaving(true);
      setError(null);
      await kreditorenApi.update(selectedKreditor.Id, editFormData);
      await loadKreditoren();
      setEditDialogOpen(false);
      setSelectedKreditor(null);
      setEditFormData({});
    } catch (err: any) {
      setError(err.message || 'Fehler beim Speichern des Kreditors');
    } finally {
      setSaving(false);
    }
  };

  const openViewDialog = (kreditor: IKreditor) => {
    setSelectedKreditor(kreditor);
    setViewDialogOpen(true);
  };

  const openEditDialog = (kreditor: IKreditor) => {
    setSelectedKreditor(kreditor);
    setEditFormData({
      Kreditorname: kreditor.Kreditorname,
      Kreditorennummer: kreditor.Kreditorennummer,
      Kontaktperson: kreditor.Kontaktperson,
      Email: kreditor.Email,
      Telefonnummer: kreditor.Telefonnummer,
      Adresse: kreditor.Adresse,
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (kreditor: IKreditor) => {
    setSelectedKreditor(kreditor);
    setDeleteDialogOpen(true);
  };

  const columns: IColumn[] = [
    {
      key: 'Kreditorname',
      name: 'Kreditorname',
      fieldName: 'Kreditorname',
      minWidth: 200,
      maxWidth: 300,
      isResizable: true,
    },
    {
      key: 'Kreditorennummer',
      name: 'Kreditorennummer',
      fieldName: 'Kreditorennummer',
      minWidth: 150,
      maxWidth: 200,
      isResizable: true,
    },
    {
      key: 'actions',
      name: 'Aktionen',
      minWidth: 120,
      maxWidth: 120,
      onRender: (item: IKreditor) => (
        <Stack horizontal tokens={{ childrenGap: 4 }}>
          <IconButton
            iconProps={{ iconName: 'View' }}
            title="Ansehen"
            onClick={() => openViewDialog(item)}
          />
          {isAdminOrEinkauf && (
            <>
              <IconButton
                iconProps={{ iconName: 'Edit' }}
                title="Bearbeiten"
                onClick={() => openEditDialog(item)}
              />
              <IconButton
                iconProps={{ iconName: 'Delete' }}
                title="Löschen"
                onClick={() => openDeleteDialog(item)}
              />
            </>
          )}
        </Stack>
      ),
    },
  ];

  const commandBarItems: ICommandBarItemProps[] = useMemo(() => {
    const items: ICommandBarItemProps[] = [];

    // "Neu" Button
    items.push({
      key: 'new',
      text: 'Neu',
      iconProps: { iconName: 'Add' },
      onClick: () => setCreateDialogOpen(true),
      disabled: !isAdminOrEinkauf,
    });

    // "Auswählen" Button
    items.push({
      key: 'select',
      text: selectionMode ? 'Auswahl beenden' : 'Auswählen',
      iconProps: { iconName: 'CheckboxComposite' },
      onClick: () => {
        setSelectionMode(!selectionMode);
        setSelectedItems([]);
      },
      disabled: !isAdminOrEinkauf,
    });

    // "Löschen" Button (nur wenn Elemente ausgewählt)
    if (selectionMode && selectedItems.length > 0 && isAdminOrEinkauf) {
      items.push({
        key: 'deleteSelected',
        text: `Löschen (${selectedItems.length})`,
        iconProps: { iconName: 'Delete' },
        onClick: () => setBulkDeleteDialogOpen(true),
      });
    }

    return items;
  }, [selectionMode, selectedItems, isAdminOrEinkauf]);

  const selection = useMemo(
    () =>
      new Selection({
        onSelectionChanged: () => {
          setSelectedItems(selection.getSelection() as IKreditor[]);
        },
      }),
    []
  );

  if (loading) {
    return (
      <Stack horizontalAlign="center" verticalAlign="center" styles={{ root: { height: '100vh' } }}>
        <Spinner label="Lade Kreditoren..." />
      </Stack>
    );
  }

  return (
    <Stack tokens={{ childrenGap: 20 }} styles={{ root: { padding: 20 } }}>
      <Text variant="xxLarge">Kreditoren</Text>

      {error && (
        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
          {error}
        </MessageBar>
      )}

      <CommandBar items={commandBarItems} />

      <Stack horizontal tokens={{ childrenGap: 10 }}>
        <SearchBox
          placeholder="Kreditoren suchen (Name, Kreditorennummer)..."
          value={searchText}
          onChange={(_, newValue) => setSearchText(newValue || '')}
          styles={{ root: { width: 400 } }}
        />
      </Stack>

      <DetailsList
        items={filteredKreditoren}
        columns={columns}
        layoutMode={DetailsListLayoutMode.justified}
        selection={selection}
        selectionMode={selectionMode ? SelectionMode.multiple : SelectionMode.none}
        selectionPreservedOnEmptyClick
      />

      {/* View Dialog */}
      <Modal
        isOpen={viewDialogOpen}
        onDismiss={() => setViewDialogOpen(false)}
        styles={centeredModalStyles}
      >
        <div style={modalContentStyles.header}>
          <Text variant="xLarge">Kreditor ansehen</Text>
          <IconButton
            iconProps={{ iconName: 'Cancel' }}
            onClick={() => setViewDialogOpen(false)}
          />
        </div>
        <div style={modalContentStyles.body}>
          {selectedKreditor && (
            <Stack tokens={{ childrenGap: 10 }}>
              <TextField label="Kreditorname" value={selectedKreditor.Kreditorname} readOnly />
              <TextField label="Kreditorennummer" value={selectedKreditor.Kreditorennummer || ''} readOnly />
              <TextField label="Kontaktperson" value={selectedKreditor.Kontaktperson || ''} readOnly />
              <TextField label="Email" value={selectedKreditor.Email || ''} readOnly />
              <TextField label="Telefonnummer" value={selectedKreditor.Telefonnummer || ''} readOnly />
              <TextField label="Adresse" value={selectedKreditor.Adresse || ''} readOnly multiline rows={2} />
            </Stack>
          )}
        </div>
        <div style={modalContentStyles.footer}>
          <PrimaryButton text="Schließen" onClick={() => setViewDialogOpen(false)} />
        </div>
      </Modal>

      {/* Edit Dialog */}
      <Modal
        isOpen={editDialogOpen}
        onDismiss={() => setEditDialogOpen(false)}
        styles={centeredModalStyles}
      >
        <div style={modalContentStyles.header}>
          <Text variant="xLarge">Kreditor bearbeiten</Text>
          <IconButton
            iconProps={{ iconName: 'Cancel' }}
            onClick={() => setEditDialogOpen(false)}
            disabled={saving}
          />
        </div>
        <div style={modalContentStyles.body}>
          <Stack tokens={{ childrenGap: 10 }}>
            <TextField
              label="Kreditorname"
              value={editFormData.Kreditorname || ''}
              onChange={(_, value) => setEditFormData((prev) => ({ ...prev, Kreditorname: value }))}
              required
            />
            <TextField
              label="Kreditorennummer"
              value={editFormData.Kreditorennummer || ''}
              onChange={(_, value) => setEditFormData((prev) => ({ ...prev, Kreditorennummer: value }))}
            />
            <TextField
              label="Kontaktperson"
              value={editFormData.Kontaktperson || ''}
              onChange={(_, value) => setEditFormData((prev) => ({ ...prev, Kontaktperson: value }))}
            />
            <TextField
              label="Email"
              value={editFormData.Email || ''}
              onChange={(_, value) => setEditFormData((prev) => ({ ...prev, Email: value }))}
            />
            <TextField
              label="Telefonnummer"
              value={editFormData.Telefonnummer || ''}
              onChange={(_, value) => setEditFormData((prev) => ({ ...prev, Telefonnummer: value }))}
            />
            <TextField
              label="Adresse"
              value={editFormData.Adresse || ''}
              onChange={(_, value) => setEditFormData((prev) => ({ ...prev, Adresse: value }))}
              multiline
              rows={2}
            />
          </Stack>
        </div>
        <div style={modalContentStyles.footer}>
          <DefaultButton text="Abbrechen" onClick={() => setEditDialogOpen(false)} disabled={saving} />
          <PrimaryButton text="Speichern" onClick={handleSaveEdit} disabled={saving} />
        </div>
      </Modal>

      {/* Delete Dialog */}
      <Dialog
        hidden={!deleteDialogOpen}
        onDismiss={() => setDeleteDialogOpen(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Kreditor löschen',
          subText: `Möchten Sie den Kreditor "${selectedKreditor?.Kreditorname}" ${selectedKreditor?.Kreditorennummer ? `(${selectedKreditor.Kreditorennummer})` : ''} wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
        }}
      >
        <DialogFooter>
          <DefaultButton text="Abbrechen" onClick={() => setDeleteDialogOpen(false)} disabled={saving} />
          <PrimaryButton text="Löschen" onClick={() => selectedKreditor && handleDeleteSingle(selectedKreditor)} disabled={saving} />
        </DialogFooter>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog
        hidden={!bulkDeleteDialogOpen}
        onDismiss={() => setBulkDeleteDialogOpen(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Kreditoren löschen',
          subText: `Möchten Sie ${selectedItems.length} Kreditoren wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
        }}
      >
        <DialogFooter>
          <DefaultButton text="Abbrechen" onClick={() => setBulkDeleteDialogOpen(false)} disabled={saving} />
          <PrimaryButton text="Löschen" onClick={handleBulkDelete} disabled={saving} />
        </DialogFooter>
      </Dialog>

      {/* Create Dialog */}
      <KreditorAnlegenDialog
        isOpen={createDialogOpen}
        onDismiss={() => setCreateDialogOpen(false)}
        onSuccess={() => {
          loadKreditoren();
          setCreateDialogOpen(false);
        }}
      />
    </Stack>
  );
};
